import axios, { AxiosRequestConfig, Method } from 'axios'
import type { AuthUserWithToken } from '~/src/hooks/useAuthUser'
import { catalogCountryFilterHeaders } from './catalogCountryFilterHeader'
import getUserToken from './getUserToken'
import logger from './logger'

export type FetchError = {
  code?: string
  status?: number
  statusText?: string
  message?: string
}

const DEFAULT_TIMEOUT = 30000

const fetcher = async <T = any>(
  url: string,
  options?: {
    method?: Method
    authUser?: AuthUserWithToken | null | undefined
    tempUserId?: string
    dataOrParams?: Record<string, any>
    axiosConfig?: AxiosRequestConfig
    logServerErrors?: boolean
  }
) => {
  const {
    method = 'POST',
    dataOrParams = {},
    axiosConfig = {},
    logServerErrors = true,
  } = options || {}
  const defaultHeaders = {}

  const headers: AxiosRequestConfig['headers'] = {
    ...defaultHeaders,
    ...catalogCountryFilterHeaders(url),
    ...axiosConfig.headers,
  }

  if (options?.authUser) {
    const idTokenResult = await getUserToken(options.authUser)
    const token = idTokenResult?.token

    if (token) {
      headers['x-authorization'] = token
    }
  }

  if (options?.tempUserId) {
    headers['x-temp-user-id'] = options.tempUserId
  }

  delete axiosConfig.headers

  try {
    const response = await axios<T>({
      url,
      method,
      timeout: DEFAULT_TIMEOUT,
      withCredentials: true,
      // Use 'data' for POST requests and 'params' for GET requests
      ...(method.toUpperCase() === 'GET'
        ? { params: dataOrParams }
        : { data: dataOrParams }),
      headers,
      ...axiosConfig, // Spread any additional Axios configuration
    })

    // Check if the status code is not within the 200 range
    if (response.status < 200 || response.status >= 300) {
      throw `An error occurred while fetching data. Status: ${response.status}`
    }

    // Simulate a slow response
    // await new Promise((resolve) => setTimeout(resolve, 5000))

    return response.data
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const errorObj = err.response?.data?.error || err.response?.data || {}
      const status = err.response?.status
      const statusText = err.response?.statusText
      const errorCode = errorObj?.code || err?.status

      // Handle both string and object error formats
      let message: string
      if (typeof errorObj === 'string') {
        // If error is a string, use it directly
        message = errorObj
      } else {
        // If error is an object, try to get the message property
        message = errorObj?.message || err?.message
      }

      // Create a user-friendly error message
      const errorMessage = message || `Request failed (${status} ${statusText})`

      const error = new Error(errorMessage) as any
      error.code = errorCode
      error.status = status
      error.statusText = statusText
      error.responseData = err.response?.data // Include full response data for handling specific error cases

      // Add request details to help with debugging
      error.url = url
      error.method = method
      error.requestData =
        method.toUpperCase() === 'GET' ? dataOrParams : undefined
      error.requestBody =
        method.toUpperCase() !== 'GET' ? dataOrParams : undefined

      // For large request bodies, only include a summary to avoid huge error objects
      if (
        error.requestBody &&
        JSON.stringify(error.requestBody).length > 1000
      ) {
        error.requestBody = {
          __summary: `Request body truncated (${Object.keys(dataOrParams).join(', ')})`,
          ...Object.fromEntries(
            Object.entries(dataOrParams)
              .slice(0, 5)
              .map(([k, v]) => [k, v])
          ),
        }
      }

      // Capture the error when status is 500 or greater
      if (logServerErrors && status && status >= 500) {
        logger.error('Error fetching data', {
          error: err,
          url,
          method,
          status,
        })
      }

      // For Axios errors, we can choose to throw a more detailed string message if available
      throw error
    }
    // Re-throw the error if it's not an Axios error
    throw typeof err === 'string' ? err : err.message
  }
}

export default fetcher
